import type { ActiveDashboard } from '@/utils/session/dashboard-from-session';

/**
 * The account settings pages this app serves, keyed by the path under
 * `/researchers/settings`, with the legacy page each one replaces. Researchers
 * outside the new UI are sent to the legacy page instead.
 */
export const ACCOUNT_SETTINGS_PAGES = {
  details: '/dashboard/settings',
  teams: '/dashboard/settings/teams',
  notifications: '/dashboard/account-settings/account-notifications',
  zoom: '/dashboard/settings/zoom',
  password: '/dashboard/account-settings/change-password',
  cookies: '/dashboard/account-settings/cookie-settings',
  calendar: '/dashboard/settings/calendar',
  'calendar-defaults': '/dashboard/settings/calendar-defaults',
} as const;

/**
 * Organization and team settings, keyed by the path under
 * `/researchers/organization-settings`, relative to the legacy team dashboard
 * (`/dashboard/organizations/:org/teams/:team`). Both member pages use the
 * newer legacy URLs: the older ones share `/settings/members`.
 */
export const WORKSPACE_SETTINGS_PAGES = {
  'organization/details': '/settings/profile',
  'organization/teams': '/settings/browse',
  'organization/members': '/organization-settings/manage-members',
  'organization/invitations': '/settings/invitations',
  'organization/sso-providers': '/settings/sso',
  'organization/sso-domains': '/settings/sso/domains',
  'team/details': '/settings/details',
  'team/members': '/team-settings/manage-members',
  'team/integrations': '/settings/integrations',
  'team/tags': '/settings/tags',
} as const;

export type AccountSettingsPage = keyof typeof ACCOUNT_SETTINGS_PAGES;
type WorkspaceSettingsPage = keyof typeof WORKSPACE_SETTINGS_PAGES;

const SETTINGS_PREFIX = '/researchers/settings';
const ORGANIZATION_SETTINGS_PREFIX = '/researchers/organization-settings';

function isAccountSettingsPage(value: string): value is AccountSettingsPage {
  return Object.hasOwn(ACCOUNT_SETTINGS_PAGES, value);
}

function isWorkspaceSettingsPage(
  value: string,
): value is WorkspaceSettingsPage {
  return Object.hasOwn(WORKSPACE_SETTINGS_PAGES, value);
}

function pageUnder(pathname: string, prefix: string): string | null {
  if (pathname !== prefix && !pathname.startsWith(`${prefix}/`)) return null;
  return pathname
    .slice(prefix.length)
    .split('/')
    .filter((part) => part.length > 0)
    .join('/');
}

/**
 * The legacy URL equivalent to a `/researchers/settings/...` or
 * `/researchers/organization-settings/...` pathname. Unknown or missing
 * sub-pages fall back to the matching landing page: the legacy account
 * details, or the legacy organization details for the active team (account
 * details again when there is no active team).
 */
export function legacySettingsUrl(
  websiteUrl: string,
  pathname: string,
  activeDashboard?: ActiveDashboard,
): string {
  const accountPage = pageUnder(pathname, SETTINGS_PREFIX);
  if (accountPage !== null && isAccountSettingsPage(accountPage)) {
    return `${websiteUrl}${ACCOUNT_SETTINGS_PAGES[accountPage]}`;
  }

  const workspacePage = pageUnder(pathname, ORGANIZATION_SETTINGS_PREFIX);
  const { organization, team } = activeDashboard ?? {};
  if (workspacePage !== null && organization && team) {
    const legacyPath = isWorkspaceSettingsPage(workspacePage)
      ? WORKSPACE_SETTINGS_PAGES[workspacePage]
      : WORKSPACE_SETTINGS_PAGES['organization/details'];
    return `${websiteUrl}/dashboard/organizations/${organization}/teams/${team}${legacyPath}`;
  }
  return `${websiteUrl}${ACCOUNT_SETTINGS_PAGES.details}`;
}
